<template>
  <div class="product-box">
    <div class="product-merge">
      <a-spin :spinning="mirrorStore.mergePreviewState"
              :delay="500"
              :tip="$t('home.MergeImageView.showTip')">
        <div class="product-preview">
          <div class="character-setting">{{ $t('home.MergeImageView.show') }}</div>
          <img :src="mirrorStore.mergePreview" />
          <div class="icons-container">
            <img :src="downloadIcon"
                 class="icon"
                 @mouseover="changeDownloadIcon(true)"
                 @mouseleave="changeDownloadIcon(false)"
                 @click="downloadImage" />
            <img :src="addIcon"
                 class="icon"
                 @click="addShoppingCart"
                 @mouseover="changeAddIcon(true)"
                 @mouseleave="changeAddIcon(false)" />
          </div>
        </div>
      </a-spin>
    </div>
    <div class="thumbnail-list">
      <div v-for="(image, index) in mirrorStore.mergePreviewArray"
           :key="index"
           class="thumbnail"
           :class="{ 'selected': index === selectedIndex, 'unselected': index !== selectedIndex }"
           @click="selectImage(image, index)">
        <!-- <img :src="image"  /> -->
        <div class="imageBox"
             :style="{ 'background-image': `url(${image})` }">
        </div>
      </div>

    </div>
  </div>
  <ShoppingCartModal ref="shoppingCart" />
  <a-modal v-model:visible="loginVisible"
           :footer="null"
           :width="450">
    <iota-simple-signin :options="{ newWindowToResetPassword: true }"
                        @success="loginSuccess" />
  </a-modal>
</template>

<script setup lang="ts">
import { spusDetail } from '../../../../shop/api/categories'
import { useMirrorStore } from '../../../index'
import type { AccountBaseInfoOption, AccountInfoService } from "../../../../account-info/index"
import { CORE_USER, type UserService } from '@/core'
import { container } from "tsyringe"
import { nextTick, ref } from "vue"
import type { Plugin } from "@/plugins"
import ShoppingCartModal from "../../../../custom-spus/view/components/ShoppingCartModal.vue"
import type { Technology } from "../../../api/interface/mirrorinterface"
import BigNumber from "bignumber.js"
import { PLUGIN_ACCOUNT_INFO } from "@/plugins/builtin/BuiltinPluginSymbol"
import { convertSpuToSimpleSpu } from "&/plugins/custom-spus/utils"
import unPreviewAddIcon from '../../../assets/mirror_imgs/unPreviewAdd.png'
import unPreviewDownloadIcon from '../../../assets/mirror_imgs/unPreviewDownLoad.png'
import previewAddIcon from '../../../assets/mirror_imgs/previewAdd.png'
import previewDownLoadIcon from '../../../assets/mirror_imgs/previewDownLoad.png'
import { useI18n } from "vue-i18n"
const accountInfoPlugin = container.resolve<Plugin<AccountBaseInfoOption, AccountInfoService>>(PLUGIN_ACCOUNT_INFO)

const { t } = useI18n()
const mirrorStore = useMirrorStore()
const calculateDiscount = accountInfoPlugin.service.calculateDiscount
const loginVisible = ref<boolean>(false)
const shoppingCart = ref<any>()
const downloadIcon = ref<string>(unPreviewDownloadIcon)
const addIcon = ref<string>(unPreviewAddIcon)
const selectedIndex = ref<number | null>(null)
const selectImage = (image: string, index: number) => {
  mirrorStore.mergePreview = image
  selectedIndex.value = index
}

const changeDownloadIcon = (isHover: boolean) => {
  downloadIcon.value = isHover ? previewDownLoadIcon : unPreviewDownloadIcon
}

const changeAddIcon = (isHover: boolean) => {
  addIcon.value = isHover ? previewAddIcon : unPreviewAddIcon
}

const downloadImage = () => {
  getUrlBase64(mirrorStore.mergePreview).then((base64) => {
    const link = document.createElement("a")
    link.href = base64 as string
    link.download = `${t('home.MergeImageView.composite')}.png`
    link.click()
  })

}

const addShoppingCart = () => {
  const userService = container.resolve<UserService>(CORE_USER)
  const user = userService.getUser()
  if (Object.entries(user.value).length < 1) {
    loginVisible.value = true
    return
  }
  openCart()
}

const loginSuccess = () => {
  loginVisible.value = !loginVisible.value
  openCart()
}

const openCart = async () => {
  const { productItem, isCustomImage } = mirrorStore
  let rebuildProductItem = productItem
  if (isCustomImage) {
    rebuildProductItem = reBuildShoopingCartData(productItem)
  }
  if (isCustomImage && rebuildProductItem.customizedImages.length && rebuildProductItem.price_detail.custom_price && rebuildProductItem.price_detail.custom_price.length) {
    rebuildProductItem.customizedImages.forEach((r: any) => {
      rebuildProductItem.price_detail.custom_price?.forEach((res: any) => {
        if (r.customableImage.name === res.name)
          res.technologies = r.customableImage.technologies
      })
    })
    const data = {
      is_custom: true,
      spu_id: rebuildProductItem.spu_id,
      name: rebuildProductItem.name,
      spu_name: rebuildProductItem.spu.name,
      customizedImages: rebuildProductItem.customizedImages,
      image: "",
      skus: rebuildProductItem.customSkus,
      custom_spu_id: rebuildProductItem.id,
      price_detail: rebuildProductItem.price_detail
    }
    shoppingCart.value.show()
    nextTick(() => {
      shoppingCart.value.setProduct(data)
    })
  } else {
    const res = await spusDetail(productItem.id as any)
    const data = {
      is_custom: false,
      spu_id: res.id,
      image: productItem.customableImages,
      skus: res.skus,
      customizedImages: null,
      name: "",
      spu_name: res.name,
      custom_spu_id: null,
      price_detail: {
        spu_price: productItem.price
      }
    }
    shoppingCart.value.show()
    nextTick(() => {
      shoppingCart.value.setProduct(data)
    })
  }
}

// Convert image URL to Base64
const getUrlBase64 = (url: string) => {
  return new Promise((resolve) => {
    const canvas = document.createElement("canvas")
    const ctx: any = canvas.getContext("2d")
    const img = new Image()
    img.crossOrigin = "Anonymous" // Allow cross-origin
    img.src = url
    img.onload = function () {
      canvas.width = img.width
      canvas.height = img.height
      ctx.drawImage(img, 0, 0)
      const dataURL = canvas.toDataURL("image/png")
      resolve(dataURL)
    }
  })
}

const reBuildShoopingCartData = (productItem: any) => {
  const data = {} as any
  const custom_price = [] as any
  data.spu_price = productItem.spu.price
  let image_price = 0
  productItem.customizedImages?.forEach((image: any) => {
    if (image.customableImage) {
      let customized_areas_scale_title = ''
      image.customableImage?.scales?.forEach((scale: any) => {
        if (scale.customized_areas_scale == image.customized_areas.customized_areas_scale) {
          customized_areas_scale_title = scale.title
        }
      })
      let minTechnologyPrice: Technology = {
        id: 0,
        name: '',
        price: 0
      }
      if (image.customableImage.technologies?.length) {
        minTechnologyPrice = image.customableImage.technologies[0]
        image.customableImage.technologies.forEach((res: Technology) => {
          if (minTechnologyPrice.price > res.price)
            minTechnologyPrice = res
        })
      }
      image_price = new BigNumber(image_price).plus(new BigNumber(image.customized_areas.discount_amount_scale || 1).times(minTechnologyPrice.price || image.customableImage.price).toFixed(2)) as any
      custom_price.push({
        id: image.customableImage.id,
        name: image.customableImage.name,
        price: image.customableImage.price,
        discount_amount_scale: image.customized_areas.discount_amount_scale || 1,
        customized_areas_scale: image.customized_areas.customized_areas_scale || 1,
        customized_areas_scale_title,
        technology: minTechnologyPrice
      })
    }
  })
  data.custom_price = custom_price
  productItem.price_detail = data
  const clothing_price = calculateDiscount("clothing", data.spu_price)
  const design_price = calculateDiscount("design", image_price)
  productItem.price = custom_price.length > 0 ? new BigNumber(clothing_price).plus(design_price).toFixed(2) : new BigNumber(clothing_price).toFixed(2)
  convertSpuToSimpleSpu(productItem)

  return productItem
}
</script>

<style scoped lang="less">
.product-box {
  display: flex;
  height: 100%;
  flex-direction: row;
}

.product-merge {
  min-height: 769px;
  flex: 1;
  display: flex;
  background: url("../../../assets/mirror_imgs/category_border.png");
  background-size: 100% 100%;
  overflow: hidden;
}

.product-preview {
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  align-items: center;
  padding: 8px;
  position: relative;

  img {
    background-color: #D5E5E6;
    object-fit: cover;
    width: 100%;
    height: 100%;
  }

  .icons-container {
    position: absolute;
    right: 20px;
    bottom: 30px;
    display: flex;
    flex-direction: column;
    align-items: flex-end;

    .icon {
      width: 45px;
      /* 根据需要调整图标大小 */
      height: 45px;
      margin-bottom: 15px;
      /* 根据需要调整图标间距 */

      &:last-child {
        margin-bottom: 0;
        /* 去掉最后一个图标的底部边距 */
      }
    }
  }
}

.character-setting {
  position: absolute;
  top: 8px;
  left: 8px;
  width: 120px;
  height: 40px;
  font-family: Microsoft YaHei;
  font-weight: bold;
  font-size: 18px;
  color: #0F171C;
  line-height: 36px;
  background-color: #5FCDD0;
  clip-path: polygon(0 0, 100% 0, 80% 100%, 0% 100%);
  padding-left: 16px;
  /* 调整此值以移动文字 */
}

.thumbnail-list {
  display: flex;
  flex-direction: column;
  padding: 16px;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 100%;
  /* 确保竖向滚动条可用 */
}

.thumbnail {
  width: 60px;
  height: 60px;
  margin-bottom: 20px;
  cursor: pointer;

  .imageBox {
    width: 60px;
    height: 60px;
    background-size: cover;
    clip-path: polygon(2px 17px,
        2px 58px,
        45px 58px,
        58px 45px,
        58px 2px,
        17px 2px)
  }
}

.selected {
  width: 60px;
  height: 60px;
  background-repeat: no-repeat;
  background-size: cover;
  background-image: url('../../../assets/mirror_imgs/selected.png');
}

.unselected {
  width: 60px;
  height: 60px;
  background-repeat: no-repeat;
  background-size: cover;
  background-image: url('../../../assets/mirror_imgs/unSelected.png');
}

.ant-spin-container {
  width: 100%;
  height: 100%;
}

:deep(.ant-spin-container) {
  width: 100%;
  height: 100%;
}

:deep(.ant-spin-nested-loading) {
  width: 100%;
}

:deep(.ant-spin-text) {
  color: #ffffff
}
</style>
